// ====================================================================================================
//  File Name   : AutonomousSwarmPage.js
//  Location    : src/features/swarm/AutonomousSwarmPage.js
//  Project     : AIRS Security - Autonomous Sentinel Ecosystem
//  Developed By: Appence Software Solution | https://appence.com
//  Legal Notice: Proprietary Property. Unauthorized use strictly prohibited. (c) 2026
// ====================================================================================================
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { API_BASE_URL } from '../../config';
import SwarmConstellationGraph from './SwarmConstellationGraph';
import {
  Bot,
  Power,
  Radio,
  MessageSquare,
  Vote,
  History,
  AlertOctagon,
  Activity,
  Layers,
  Zap,
  Wifi,
  WifiOff,
  Monitor,
  Search,
  Clock,
  ShieldCheck,
  Key,
  X,
  Laptop,
  Smartphone,
  Server as ServerIcon,
  Copy,
  Check
} from 'lucide-react';

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return 'N/A';
  }
};

const getOsIcon = (os) => {
  const o = String(os || '').toUpperCase();
  if (o.includes('WIN')) return <Laptop className="w-3.5 h-3.5 text-blue-400" />;
  if (o.includes('ANDRO')) return <Smartphone className="w-3.5 h-3.5 text-emerald-400" />;
  return <ServerIcon className="w-3.5 h-3.5 text-amber-400" />;
};

const getOsBadgeColor = (os) => {
  const o = String(os || '').toUpperCase();
  if (o.includes('WIN')) return 'bg-blue-950 text-blue-300 border-blue-800';
  if (o.includes('MAC') || o.includes('DARWIN')) return 'bg-purple-950 text-purple-300 border-purple-800';
  if (o.includes('ANDRO')) return 'bg-emerald-950 text-emerald-300 border-emerald-800';
  return 'bg-amber-950 text-amber-300 border-amber-800';
};

const AutonomousSwarmPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('roster');
  const [selectedDevice, setSelectedDevice] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [osFilter, setOsFilter] = useState('ALL');
  const [activeOnly, setActiveOnly] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  // 1. Fetch Swarm Status with Multi-Tenant Device Filtering
  const { data: statusData = {} } = useQuery({
    queryKey: ['swarmStatus', selectedDevice],
    queryFn: async () => {
      const url = selectedDevice === 'ALL'
        ? '/admin/swarm/status'
        : `/admin/swarm/status?device_id=${encodeURIComponent(selectedDevice)}`;
      const res = await axiosClient.get(url);
      return res.data || res;
    },
    refetchInterval: wsConnected ? 8000 : 3000
  });

  // 2. Fetch Inter-Agent Debates
  const { data: debatesData = { debates: [] } } = useQuery({
    queryKey: ['swarmDebates'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/swarm/debates?limit=30');
      return res.data || res;
    },
    refetchInterval: 5000,
    enabled: activeTab === 'debates'
  });

  // 3. Fetch Full Audit Ledger with Device Filter
  const { data: logsData = { logs: [] } } = useQuery({
    queryKey: ['swarmLogs', selectedDevice],
    queryFn: async () => {
      const url = selectedDevice === 'ALL'
        ? '/admin/swarm/logs?limit=50'
        : `/admin/swarm/logs?limit=50&device_id=${encodeURIComponent(selectedDevice)}`;
      const res = await axiosClient.get(url);
      return res.data || res;
    },
    refetchInterval: 6000,
    enabled: activeTab === 'ledger'
  });

  // 4. Real-Time WebSocket Pipeline
  useEffect(() => {
    let isMounted = true;
    let reconnectTimer = null;

    const getWsUrl = () => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token') || '';
      let base = API_BASE_URL || 'http://localhost:8000/api/v1';
      let wsBase = base.replace(/^http/, 'ws');
      if (wsBase.endsWith('/')) wsBase = wsBase.slice(0, -1);
      return `${wsBase}/admin/swarm/ws?token=${encodeURIComponent(token)}`;
    };

    const connectWs = () => {
      if (!isMounted) return;

      try {
        const ws = new WebSocket(getWsUrl());
        wsRef.current = ws;

        ws.onopen = () => {
          if (isMounted) setWsConnected(true);
        };

        ws.onmessage = (event) => {
          if (!isMounted) return;
          try {
            const data = JSON.parse(event.data);
            if (['SENTINEL_SPAWNED', 'SENTINEL_RETIRED', 'SWARM_EMERGENCY_HALT', 'SWARM_FABRIC_ENABLED'].includes(data.event)) {
              queryClient.invalidateQueries(['swarmStatus']);
              queryClient.invalidateQueries(['swarmDebates']);
              queryClient.invalidateQueries(['swarmLogs']);
            }
          } catch (e) { }
        };

        ws.onclose = () => {
          if (isMounted) {
            setWsConnected(false);
            reconnectTimer = setTimeout(connectWs, 4000);
          }
        };

        ws.onerror = () => { };
      } catch (e) {
        if (isMounted) {
          setWsConnected(false);
          reconnectTimer = setTimeout(connectWs, 4000);
        }
      }
    };

    connectWs();

    return () => {
      isMounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [queryClient]);

  // Master Toggle Mutation
  const toggleMutation = useMutation({
    mutationFn: async (enabled) => {
      const res = await axiosClient.post('/admin/swarm/toggle', { enabled, admin_user: 'COMMANDER' });
      return res.data || res;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['swarmStatus']);
      setActionNotice({ type: 'success', msg: data.message });
      setTimeout(() => setActionNotice(null), 5000);
    }
  });

  // Hard Kill-Switch Mutation
  const killSwitchMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosClient.post('/admin/swarm/terminate-all');
      return res.data || res;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['swarmStatus']);
      setActionNotice({ type: 'danger', msg: `EMERGENCY PURGE EXECUTED: ${data.terminated_agents_count} sub-agents terminated across fleet.` });
      setTimeout(() => setActionNotice(null), 6000);
    }
  });

  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const isEnabled = statusData.is_swarm_enabled ?? true;
  const activeCount = statusData.active_agents_count || 0;
  const retiredCount = statusData.retired_agents_count || 0;
  const killedCount = statusData.emergency_killed_count || 0;
  const capacity = statusData.host_capacity || {};
  const fleetSummary = statusData.fleet_summary || [];

  // 5. High-Performance Client Filter Engine (Search 1,000+ Nodes Instantly)
  const filteredFleet = useMemo(() => {
    return fleetSummary.filter((node) => {
      // Search Match
      const search = searchTerm.trim().toLowerCase();
      const matchesSearch = !search || (
        (node.hostname && node.hostname.toLowerCase().includes(search)) ||
        (node.license_key && node.license_key.toLowerCase().includes(search)) ||
        (node.host_agent_id && node.host_agent_id.toLowerCase().includes(search))
      );

      // OS Filter Match
      const os = String(node.os_target || '').toUpperCase();
      let matchesOs = true;
      if (osFilter === 'WINDOWS') matchesOs = os.includes('WIN');
      else if (osFilter === 'LINUX') matchesOs = os.includes('LINUX') || os.includes('UBUNTU');
      else if (osFilter === 'ANDROID') matchesOs = os.includes('ANDRO');
      else if (osFilter === 'MACOS') matchesOs = os.includes('MAC') || os.includes('DARWIN');

      // Active Swarm Only Filter
      const matchesActive = !activeOnly || Number(node.micro_agents_count || 0) > 0;

      return matchesSearch && matchesOs && matchesActive;
    });
  }, [fleetSummary, searchTerm, osFilter, activeOnly]);

  return (
    <div className="p-6 space-y-6 bg-[#020617] text-slate-100 min-h-screen font-sans">

      {/* Top Header Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-xl text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.25)]">
            <Bot className="w-7 h-7 animate-pulse" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-wide font-mono flex items-center gap-2">
              AUTONOMOUS SWARM FABRIC
              <span className={`text-[10px] px-2 py-0.5 rounded font-mono uppercase ${isEnabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                }`}>
                {isEnabled ? 'SYSTEM ACTIVE' : 'SWARM HALTED'}
              </span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1 font-mono ${wsConnected ? 'bg-cyan-950 text-cyan-300 border border-cyan-800' : 'bg-slate-900 text-slate-500 border border-slate-800'
                }`}>
                {wsConnected ? <Wifi className="w-3 h-3 text-cyan-400" /> : <WifiOff className="w-3 h-3 text-slate-500" />}
                {wsConnected ? 'WS STREAM' : 'HTTP SYNC'}
              </span>
            </h1>
            <p className="text-xs text-slate-400 font-mono mt-0.5">
              Enterprise Fleet Swarm Governance & Real-Time Threat Quorum
            </p>
          </div>
        </div>

        {/* Master Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => toggleMutation.mutate(!isEnabled)}
            disabled={toggleMutation.isPending}
            className={`px-4 py-2 rounded-xl font-mono text-xs font-bold flex items-center gap-2 transition-all border ${isEnabled
                ? 'bg-emerald-950/80 hover:bg-emerald-900 border-emerald-500/40 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                : 'bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-400'
              }`}
          >
            <Power className="w-4 h-4" />
            {isEnabled ? 'SWARM FABRIC: ON' : 'SWARM FABRIC: OFF'}
          </button>

          <button
            onClick={() => {
              if (window.confirm("⚠️ EMERGENCY KILL-SWITCH WARNING:\nThis will instantly terminate all running micro-sentinels across the fleet. Proceed?")) {
                killSwitchMutation.mutate();
              }
            }}
            disabled={killSwitchMutation.isPending || activeCount === 0}
            className="px-4 py-2 bg-rose-950/80 hover:bg-rose-900 border border-rose-500/50 text-rose-300 rounded-xl font-mono text-xs font-bold flex items-center gap-2 transition-all shadow-[0_0_15px_rgba(239,68,68,0.25)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <AlertOctagon className="w-4 h-4 text-rose-400" />
            HARD KILL-SWITCH
          </button>
        </div>
      </div>

      {actionNotice && (
        <div className={`p-3 rounded-xl border text-xs font-mono flex items-center gap-2 ${actionNotice.type === 'danger' ? 'bg-rose-950/90 border-rose-500/50 text-rose-300' : 'bg-emerald-950/90 border-emerald-500/50 text-emerald-300'
          }`}>
          <Zap className="w-4 h-4 shrink-0" />
          <span>{actionNotice.msg}</span>
        </div>
      )}

      {/* 🔍 FLEET DISCOVERY & SEARCH BAR (Handles 1,000+ Nodes) */}
      <div className="bg-[#0b1329] border border-slate-800/90 p-4 rounded-2xl space-y-3 font-mono shadow-xl">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">

          {/* Real-time Search Input */}
          <div className="relative flex-1 w-full max-w-lg">
            <Search className="w-4 h-4 text-cyan-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by License Key (e.g. RS-3BCF...), Hostname, or IP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 focus:border-cyan-500 rounded-xl pl-9 pr-8 py-2 text-xs text-slate-200 placeholder-slate-500 outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* OS Platform & Status Filters */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {/* OS Filter Chips */}
            <div className="flex items-center bg-slate-950 border border-slate-800 p-0.5 rounded-lg text-[10px]">
              {['ALL', 'WINDOWS', 'LINUX', 'ANDROID'].map((os) => (
                <button
                  key={os}
                  onClick={() => setOsFilter(os)}
                  className={`px-2 py-1 rounded transition-all ${osFilter === os
                      ? 'bg-cyan-950 text-cyan-300 font-bold border border-cyan-800/60 shadow-[0_0_8px_rgba(6,182,212,0.3)]'
                      : 'text-slate-400 hover:text-slate-200'
                    }`}
                >
                  {os}
                </button>
              ))}
            </div>

            {/* Active Sentinels Only Toggle */}
            <button
              onClick={() => setActiveOnly(!activeOnly)}
              className={`px-2.5 py-1 rounded-lg border text-[10px] flex items-center gap-1.5 transition-all ${activeOnly
                  ? 'bg-emerald-950 text-emerald-300 border-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]'
                  : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-slate-200'
                }`}
            >
              <Radio className="w-3 h-3 text-emerald-400" />
              Active Sentinels Only
            </button>

            {/* Global Reset */}
            {selectedDevice !== 'ALL' && (
              <button
                onClick={() => { setSelectedDevice('ALL'); setSearchTerm(''); setOsFilter('ALL'); setActiveOnly(false); }}
                className="px-2.5 py-1 rounded-lg bg-cyan-950/60 border border-cyan-800/40 text-cyan-300 text-[10px] hover:underline"
              >
                Reset to Global Fleet
              </button>
            )}
          </div>
        </div>

        {/* Filtered Fleet Node Grid / Chips */}
        <div className="pt-2 border-t border-slate-850 flex flex-wrap gap-2.5 max-h-48 overflow-y-auto">
          {filteredFleet.map((node, i) => {
            const isSelected = selectedDevice === node.host_agent_id;
            const hasSentinels = Number(node.micro_agents_count || 0) > 0;
            return (
              <div
                key={i}
                className={`p-2.5 rounded-xl border text-xs flex flex-col justify-between gap-1.5 transition-all cursor-pointer min-w-[210px] max-w-[260px] ${isSelected
                    ? 'bg-cyan-950/80 border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                    : 'bg-slate-950 hover:bg-slate-900 border-slate-800'
                  }`}
                onClick={() => setSelectedDevice(node.host_agent_id)}
              >
                {/* Node Title & OS Badge */}
                <div className="flex justify-between items-start gap-2">
                  <div className="font-bold text-slate-200 truncate flex items-center gap-1.5">
                    {getOsIcon(node.os_target)}
                    <span className="truncate">{node.hostname}</span>
                  </div>
                  <span className={`px-1.5 py-0.2 rounded text-[8px] font-bold border shrink-0 ${getOsBadgeColor(node.os_target)}`}>
                    {node.os_target}
                  </span>
                </div>

                {/* License Key with Copy Button */}
                <div className="flex items-center justify-between text-[10px] bg-black/40 px-2 py-0.5 rounded border border-slate-850">
                  <div className="text-slate-400 flex items-center gap-1 font-mono truncate" title={node.license_key || 'No License Key'}>
                    <Key className="w-2.5 h-2.5 text-amber-400 shrink-0" />
                    <span className="truncate">{node.license_key ? `${node.license_key.slice(0, 10)}...` : 'UNLICENSED'}</span>
                  </div>
                  {node.license_key && (
                    <button
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(node.license_key, node.host_agent_id); }}
                      className="text-slate-500 hover:text-cyan-300 ml-1"
                      title="Copy Full License Key"
                    >
                      {copiedKey === node.host_agent_id ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  )}
                </div>

                {/* Status & Active Sentinels Counter */}
                <div className="flex justify-between items-center text-[10px] pt-1">
                  <span className={`flex items-center gap-1 ${node.device_status === 'online' ? 'text-emerald-400' : 'text-slate-500'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${node.device_status === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`}></span>
                    {node.device_status}
                  </span>
                  <span className={`px-2 py-0.5 rounded font-bold ${hasSentinels ? 'bg-emerald-950 text-emerald-300 border border-emerald-700' : 'bg-slate-900 text-slate-500'
                    }`}>
                    {node.micro_agents_count || 0} Sentinels
                  </span>
                </div>
              </div>
            );
          })}

          {filteredFleet.length === 0 && (
            <div className="p-4 text-center text-slate-500 text-xs italic w-full">
              No matching endpoints found for query "{searchTerm}". Verify license key or platform filters.
            </div>
          )}
        </div>
      </div>

      {/* 🚀 60 FPS VISUAL CONSTELLATION GRAPH */}
      <SwarmConstellationGraph
        activeAgents={statusData.active_roster || []}
        recentMessages={debatesData.debates || []}
      />

      {/* Metric Telemetry Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 font-mono">
        <div className="bg-[#0b1329] border border-cyan-950/80 p-4 rounded-xl">
          <div className="text-[11px] text-slate-400 uppercase tracking-wider">Active Micro-Sentinels</div>
          <div className="text-2xl font-bold text-emerald-400 mt-1 flex items-center gap-2">
            {activeCount}
            {activeCount > 0 && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Live executing in isolated sandbox</div>
        </div>

        <div className="bg-[#0b1329] border border-cyan-950/80 p-4 rounded-xl">
          <div className="text-[11px] text-slate-400 uppercase tracking-wider">Retired (Completed)</div>
          <div className="text-2xl font-bold text-cyan-400 mt-1">{retiredCount}</div>
          <div className="text-[10px] text-slate-500 mt-1">Objective fulfilled & memory purged</div>
        </div>

        <div className="bg-[#0b1329] border border-cyan-950/80 p-4 rounded-xl">
          <div className="text-[11px] text-slate-400 uppercase tracking-wider">Emergency Terminated</div>
          <div className="text-2xl font-bold text-rose-400 mt-1">{killedCount}</div>
          <div className="text-[10px] text-slate-500 mt-1">Purged via admin kill-switch</div>
        </div>

        <div className="bg-[#0b1329] border border-cyan-950/80 p-4 rounded-xl">
          <div className="text-[11px] text-slate-400 uppercase tracking-wider">Selected Fleet Focus</div>
          <div className="text-xs font-bold text-amber-300 mt-1 truncate">
            {selectedDevice === 'ALL' ? `Global Fleet (${fleetSummary.length} Nodes)` : `Node: ${statusData.active_roster?.[0]?.hostname || 'Focused Node'}`}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 flex justify-between">
            <span>Target OS: {statusData.active_roster?.[0]?.os_target || 'MULTI-OS'}</span>
            <span>Max Cap: {capacity.max_allowed_agents || 6} Agents</span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex border-b border-slate-800 gap-6 text-xs font-mono">
        <button
          onClick={() => setActiveTab('roster')}
          className={`pb-3 flex items-center gap-2 font-bold transition-all border-b-2 ${activeTab === 'roster' ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
        >
          <Layers className="w-4 h-4" /> ACTIVE SWARM ROSTER ({activeCount})
        </button>

        <button
          onClick={() => setActiveTab('debates')}
          className={`pb-3 flex items-center gap-2 font-bold transition-all border-b-2 ${activeTab === 'debates' ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
        >
          <MessageSquare className="w-4 h-4" /> INTER-AGENT DEBATE & CHAT MESH
        </button>

        <button
          onClick={() => setActiveTab('consensus')}
          className={`pb-3 flex items-center gap-2 font-bold transition-all border-b-2 ${activeTab === 'consensus' ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
        >
          <Vote className="w-4 h-4" /> QUORUM DECISIONS (VOTES)
        </button>

        <button
          onClick={() => setActiveTab('ledger')}
          className={`pb-3 flex items-center gap-2 font-bold transition-all border-b-2 ${activeTab === 'ledger' ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
        >
          <History className="w-4 h-4" /> IMMUTABLE AUDIT LEDGER
        </button>
      </div>

      {/* TAB 1: ACTIVE ROSTER */}
      {activeTab === 'roster' && (
        <div className="bg-[#0b1329] border border-slate-800 rounded-xl overflow-hidden font-mono">
          <div className="p-3.5 border-b border-slate-800 bg-slate-950/60 flex justify-between items-center text-xs">
            <span className="font-bold text-slate-300 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-400" /> LIVE EXECUTING MICRO-SENTINELS
            </span>
            <span className="text-[10px] text-slate-500">Auto-refreshing via WebSocket</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950/80 text-[10px] text-slate-400 uppercase border-b border-slate-800">
                <tr>
                  <th className="p-3">Agent Identifier</th>
                  <th className="p-3">Host Machine & OS</th>
                  <th className="p-3">Specialized Role</th>
                  <th className="p-3">Target Scope</th>
                  <th className="p-3">Operational Objective</th>
                  <th className="p-3">Spawn Time (Date & Time)</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-850">
                {(statusData.active_roster || []).map((agent, idx) => (
                  <tr key={idx} className="hover:bg-slate-900/60 transition-colors">
                    <td className="p-3 font-bold text-cyan-300 flex items-center gap-2">
                      <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                      {agent.agent_name}
                      <span className="text-[9px] text-slate-500">({agent.agent_uuid?.slice(-6)})</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        {getOsIcon(agent.os_target)}
                        <span className="font-semibold text-slate-200">{agent.hostname || 'Primary Node'}</span>
                        <span className={`px-1.5 py-0.2 rounded text-[9px] border ${getOsBadgeColor(agent.os_target)}`}>
                          {agent.os_target}
                        </span>
                      </div>
                      <div className="text-[9px] text-slate-500 truncate max-w-[140px]" title={agent.host_agent_id}>
                        ID: {agent.host_agent_id?.slice(0, 8)}...
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 rounded text-[10px] bg-cyan-950 text-cyan-300 border border-cyan-800/50">
                        {agent.agent_type}
                      </span>
                    </td>
                    <td className="p-3 font-semibold text-amber-300">{agent.target_scope}</td>
                    <td className="p-3 text-[11px] text-slate-300 max-w-xs truncate" title={agent.objective}>{agent.objective}</td>
                    <td className="p-3 text-[11px] text-slate-400 font-mono flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-cyan-500" />
                      {formatDateTime(agent.created_at)}
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-950 text-emerald-400 border border-emerald-800">
                        ● {agent.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {(!statusData.active_roster || statusData.active_roster.length === 0) && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-500 italic">
                      Zero micro-sentinels in orbit for this host. Central AGI is currently operating in passive vigilance mode.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: INTER-AGENT DEBATE & CHAT MESH */}
      {activeTab === 'debates' && (
        <div className="bg-[#0b1329] border border-slate-800 rounded-xl overflow-hidden font-mono">
          <div className="p-3.5 border-b border-slate-800 bg-slate-950/60 flex justify-between items-center text-xs">
            <span className="font-bold text-slate-300 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-cyan-400" /> LIVE INTER-AGENT CONSENSUS MESH STREAM
            </span>
            <span className="text-[10px] text-slate-500">Live Peer-to-Peer Planning</span>
          </div>

          <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto text-xs">
            {(debatesData.debates || []).map((msg, idx) => (
              <div key={idx} className="bg-slate-950/70 border border-slate-800/80 p-3 rounded-lg space-y-1">
                <div className="flex justify-between items-center text-[10px]">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-cyan-400">{msg.sender_agent_uuid?.slice(-10)}</span>
                    <span className="text-slate-500">──►</span>
                    <span className="font-bold text-amber-300">{msg.recipient_agent_uuid?.slice(-10)}</span>
                    <span className="text-slate-500">on</span>
                    <span className="text-slate-300 font-semibold">{msg.hostname || 'Primary Node'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-[10px] flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> {formatDateTime(msg.created_at)}
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-400 text-[9px]">
                      {msg.message_type}
                    </span>
                  </div>
                </div>
                <div className="bg-black/50 p-2 rounded text-[11px] text-slate-300 font-mono overflow-x-auto">
                  {typeof msg.payload === 'object' ? JSON.stringify(msg.payload, null, 2) : msg.payload}
                </div>
              </div>
            ))}
            {(!debatesData.debates || debatesData.debates.length === 0) && (
              <div className="p-8 text-center text-slate-500 italic">Zero inter-agent messages recorded.</div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: QUORUM DECISIONS */}
      {activeTab === 'consensus' && (
        <div className="bg-[#0b1329] border border-slate-800 rounded-xl overflow-hidden font-mono">
          <div className="p-3.5 border-b border-slate-800 bg-slate-950/60 text-xs font-bold text-slate-300 flex items-center gap-2">
            <Vote className="w-4 h-4 text-amber-400" /> SWARM QUORUM VOTING & MITIGATION VERDICTS
          </div>

          <div className="p-4 space-y-3 text-xs">
            {(statusData.recent_consensus_decisions || []).map((dec, idx) => (
              <div key={idx} className="bg-slate-950/80 border border-slate-800 p-3.5 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                <div>
                  <div className="text-cyan-300 font-bold flex items-center gap-2">
                    Threat Target: {dec.target_threat_id}
                    <span className="text-[10px] text-slate-400 font-normal">on {dec.hostname || 'Primary Node'}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Proposed: <span className="text-amber-300">{dec.proposed_action}</span> | Verdict: <span className="text-emerald-400 font-bold">{dec.final_verdict}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" /> Decided At: {formatDateTime(dec.created_at)}
                  </div>
                </div>

                <div className="flex items-center gap-4 text-right">
                  <div className="text-[11px]">
                    <span className="text-emerald-400 font-bold">{dec.votes_for} For</span> / <span className="text-rose-400 font-bold">{dec.votes_against} Against</span>
                  </div>
                  <span className={`px-2.5 py-1 rounded text-[10px] font-bold ${dec.consensus_reached ? 'bg-emerald-950 text-emerald-300 border border-emerald-700' : 'bg-rose-950 text-rose-300 border border-rose-700'
                    }`}>
                    {dec.consensus_reached ? 'CONSENSUS REACHED ✓' : 'VETOED ✗'}
                  </span>
                </div>
              </div>
            ))}
            {(!statusData.recent_consensus_decisions || statusData.recent_consensus_decisions.length === 0) && (
              <div className="p-8 text-center text-slate-500 italic">No voting rounds recorded yet.</div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: IMMUTABLE AUDIT LEDGER */}
      {activeTab === 'ledger' && (
        <div className="bg-[#0b1329] border border-slate-800 rounded-xl overflow-hidden font-mono">
          <div className="p-3.5 border-b border-slate-800 bg-slate-950/60 text-xs font-bold text-slate-300 flex items-center gap-2">
            <History className="w-4 h-4 text-cyan-400" /> CRYPTOGRAPHIC LIFECYCLE AUDIT TRAIL (HMAC-SHA256)
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950/80 text-[10px] text-slate-400 uppercase border-b border-slate-800">
                <tr>
                  <th className="p-3">Agent Name</th>
                  <th className="p-3">Host Machine & OS</th>
                  <th className="p-3">Specialized Role</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Spawn Time</th>
                  <th className="p-3">Retirement Time</th>
                  <th className="p-3">Spawn Trigger & Reason</th>
                  <th className="p-3">Signature</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-850">
                {(logsData.logs || []).map((l, idx) => (
                  <tr key={idx} className="hover:bg-slate-900/60 text-[11px]">
                    <td className="p-3 font-bold text-cyan-400">{l.agent_name}</td>
                    <td className="p-3">
                      <div className="font-semibold text-slate-200 flex items-center gap-1">
                        {getOsIcon(l.os_target)}
                        {l.hostname || 'Primary Node'}
                      </div>
                      <span className={`px-1.5 py-0.2 rounded text-[9px] border ${getOsBadgeColor(l.os_target)}`}>
                        {l.os_target || 'LINUX'}
                      </span>
                    </td>
                    <td className="p-3">{l.agent_type}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-[9px] ${l.status === 'ACTIVE'
                          ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                          : l.status === 'RETIRED'
                            ? 'bg-cyan-950 text-cyan-400 border border-cyan-800'
                            : 'bg-rose-950 text-rose-400 border border-rose-800'
                        }`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="p-3 text-[10px] text-slate-300 font-mono">{formatDateTime(l.created_at)}</td>
                    <td className="p-3 text-[10px] text-slate-400 font-mono">{l.terminated_at ? formatDateTime(l.terminated_at) : 'Active'}</td>
                    <td className="p-3 max-w-xs truncate text-slate-400" title={`${l.spawn_trigger} | ${l.termination_reason || ''}`}>
                      {l.termination_reason ? `Terminated: ${l.termination_reason}` : l.spawn_trigger}
                    </td>
                    <td className="p-3 font-mono text-[9px] text-slate-500">{l.integrity_signature?.slice(0, 16)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default AutonomousSwarmPage;